// Э-2. График отгрузок: слева заявки дня с прогрессом, справа рейсы на шкале по каждой РБУ всех заводов.

import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../shared/api";
import { addDays, fmtDay, fmtNum, fmtTime, localISODate, minutesOfDay, zonedISO } from "../../shared/format";
import { useLive } from "../../shared/live";
import { setQuery, useRoute } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Empty, Legend, Loading, ReasonDialog, useAsync, useInterval, useToast } from "../../shared/ui";
import { OrderCard } from "../components/OrderCard";
import { OrderForm } from "../components/OrderForm";
import { api, can, live, ORDER_STATUS, ORDER_TONE, TRIP_LEGEND, tripSwatch, useCtx } from "../ctx";

const PX_PER_MIN = 1.25;
const DAY_MIN = 24 * 60;

type Trip = {
  id: string; order_id: string; order_number: number; customer_name: string; site_name: string; seq: number;
  trips_total: number; unit_id: string; plant_id: string; grade: string; volume_m3: number; slot_start: string;
  slot_end: string; planned_arrival: string; status: string; vehicle_plate: string | null; driver_name?: string | null; conflict_override: boolean;
  timing_level: string | null; deviation_minutes: number | null; version: number;
};
type DayView = {
  date: string; now: string;
  units: { id: string; name: string; plant_id: string; plant_name: string; capacity_m3: number; load_by_hour: number[]; downtimes: { start: string; end: string }[] }[];
  trips: Trip[];
  orders: { id: string; number: number; status: string; customer_name: string; site_name: string; grade: string; volume_m3: number; desired_start: string; short_notice: boolean; lab_status: string; progress: { pct: number; shipped_m3: number; remaining_m3: number } }[];
  indicator: { ordered_m3: number; capacity_m3: number; reserve_m3: number; load_pct: number; overloaded: boolean; overloaded_hours: string[] };
  conflicts: string[];
};

export function SchedulePage() {
  const { me } = useCtx();
  const toast = useToast();
  const route = useRoute();
  const date = route.query.get("date") || localISODate();
  const openOrder = route.query.get("order");
  const isNew = route.query.get("new") === "1";
  const day = useAsync<DayView>(() => api.get("/scheduling/day", { date }), [date]);
  const alerts = useAsync<any[]>(() => api.get("/alerts"), [date]);
  const [selected, setSelected] = useState<string | null>(null);
  // замечание 15.10 №5: рейсы открытой заявки остаются подсвеченными и после закрытия карточки
  const [focus, setFocus] = useState<string | null>(null);
  useEffect(() => { if (openOrder) setFocus(openOrder); }, [openOrder]);
  const [pendingMove, setPendingMove] = useState<{ trip: Trip; unit: string; start: string } | null>(null);
  const [showAlerts, setShowAlerts] = useState(false);
  useLive(live, ["order.", "trip.", "unit.", "batch."], () => { day.reload(); alerts.reload(); });
  useInterval(() => day.reload(), 60_000); // линия «сейчас» и риски опоздания двигаются со временем

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n" && can.createOrder(me.role)) {
        e.preventDefault();
        setQuery({ new: 1, order: null });
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [me.role]);

  const move = async (trip: Trip, unit: string, start: string, reason?: string) => {
    try {
      await api.post(`/trips/${trip.id}/move`, { version: trip.version, unit_id: unit, slot_start: start, allow_conflict: !!reason, reason });
      toast.ok(reason ? "Рейс перенесён с наложением, причина сохранена" : "Рейс перенесён");
      day.reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.details.conflicts) setPendingMove({ trip, unit, start });
      else toast.error(e);
      day.reload();
    }
  };

  const v = day.data;
  return (
    <>
      <TopBar title="График отгрузок">
        <div className="row tight">
          <Button className="icon-btn" aria-label="Предыдущий день" onClick={() => setQuery({ date: addDays(date, -1) })}>‹</Button>
          <input className="input" type="date" value={date} style={{ width: 160 }} onChange={(e) => setQuery({ date: e.target.value })} aria-label="Дата" />
          <Button className="icon-btn" aria-label="Следующий день" onClick={() => setQuery({ date: addDays(date, 1) })}>›</Button>
          {date !== localISODate() && <Button size="small" variant="ghost" onClick={() => setQuery({ date: null })}>Сегодня</Button>}
        </div>
        {v && (
          <div className="row small" style={{ gap: 18 }}>
            <span>Заказано <b className="num">{fmtNum(v.indicator.ordered_m3, 0)} м³</b></span>
            <span>Загрузка РБУ <b className="num">{fmtNum(v.indicator.load_pct, 0)}%</b></span>
            <span>Резерв <b className="num">{fmtNum(v.indicator.reserve_m3, 0)} м³</b></span>
            {v.indicator.overloaded && <span className="tag s-conflict">Перегруз</span>}
          </div>
        )}
        {alerts.data && alerts.data.length > 0 && (
          <Button size="small" onClick={() => setShowAlerts((x) => !x)} aria-expanded={showAlerts}>
            Предупреждения: {alerts.data.length}
          </Button>
        )}
        {can.createOrder(me.role) && (
          <Button variant="primary" onClick={() => setQuery({ new: 1, order: null })} title="Ctrl+N">+ Заявка</Button>
        )}
      </TopBar>
      <div className="content">
        {showAlerts && alerts.data && (
          <div className="stack tight">
            {alerts.data.map((a, i) => (
              <div key={i} className={`alert${a.level === "late" ? " risk" : ""}`}>
                <button className="btn ghost small" style={{ float: "right" }} onClick={() => a.order_id && setQuery({ order: a.order_id })}>Открыть</button>
                <div className="t">{a.text}</div>
              </div>
            ))}
          </div>
        )}
        {!v ? <Loading error={day.error} onRetry={day.reload} /> : (
          <div className="split" style={{ gridTemplateColumns: "300px 1fr" }}>
            <OrdersList view={v} selected={openOrder || focus} onOpen={(id) => setQuery({ order: id, new: null })} />
            <div className="panel">
              <Timeline view={v} selected={selected} order={focus} onSelect={(t) => { setSelected(t.id); setQuery({ order: t.order_id, new: null }); }}
                onMove={can.dispatch(me.role) ? move : undefined} />
              <div className="pb row between">
                {focus && v.orders.some((o) => o.id === focus) && (
                  <span className="tag plain">Подсвечены рейсы заявки №{v.orders.find((o) => o.id === focus)!.number}
                    <button className="link" style={{ marginLeft: 6 }} aria-label="Снять подсветку" onClick={() => setFocus(null)}>✕</button></span>
                )}
                <Legend items={TRIP_LEGEND} />
                <span className="muted small">
                  {can.dispatch(me.role) ? "Рейс перетаскивается мышью; конфликт сохраняется только с причиной" : "Пунктир — риск опоздания, красная рамка — опоздание"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      {isNew && <OrderForm onClose={() => setQuery({ new: null })} onSaved={(id) => { setQuery({ new: null, order: id || null }); day.reload(); }} />}
      {openOrder && !isNew && <OrderCard id={openOrder} onClose={() => setQuery({ order: null })} onChanged={() => day.reload()} />}
      {pendingMove && (
        <ReasonDialog title="Наложение на другие погрузки" action="Сохранить с наложением" danger
          onCancel={() => setPendingMove(null)}
          onConfirm={async (reason) => { const p = pendingMove; setPendingMove(null); await move(p.trip, p.unit, p.start, reason); }} />
      )}
    </>
  );
}

function OrdersList({ view, selected, onOpen }: { view: DayView; selected: string | null; onOpen: (id: string) => void }) {
  const orders = view.orders.filter((o) => o.status !== "cancelled" && o.status !== "rescheduled");
  return (
    <div className="panel" style={{ maxHeight: "calc(100vh - 150px)", overflow: "auto" }}>
      <div className="ph"><h2>Заявки дня</h2><span className="muted">{orders.length}</span></div>
      {orders.length === 0 && <Empty>На этот день заявок нет. Новую заявку можно создать кнопкой «+ Заявка».</Empty>}
      {orders.map((o) => (
        <div key={o.id} className={`order-row${selected === o.id ? " active" : ""}`} onClick={() => onOpen(o.id)}
          role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(o.id)}>
          <div className="t1"><span>{o.customer_name}</span><span className="num">{fmtTime(o.desired_start)}</span></div>
          <div className="small muted">{o.site_name}, {o.grade}, {fmtNum(o.volume_m3)} м³</div>
          {/* О-5 (22.10.2026): остаток — в строке прогресса, всегда на своём месте; ярлыки — отдельной строкой слева */}
          <div className="order-progress small">
            <div className="bar thin"><i style={{ width: `${o.progress.pct}%`, background: o.progress.pct >= 100 ? "var(--st-done)" : "var(--st-transit)" }} /></div>
            <span className="muted num">осталось {fmtNum(o.progress.remaining_m3)} м³</span>
          </div>
          <div className="order-tags small">
            <span className={`tag ${ORDER_TONE[o.status] || ""}`}>{ORDER_STATUS[o.status]}</span>
            {o.short_notice && <span className="tag plain">срочная</span>}
            {o.lab_status === "pending" && <span className="tag s-loading">ждёт лабораторию</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Timeline({ view, selected, order, onSelect, onMove }: {
  view: DayView; selected: string | null; order?: string | null; onSelect: (t: Trip) => void; onMove?: (t: Trip, unit: string, startISO: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const grab = useRef<{ trip: Trip; offset: number } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const width = DAY_MIN * PX_PER_MIN;
  const nowMin = minutesOfDay(view.now, view.date);
  const byUnit = useMemo(() => {
    const m: Record<string, Trip[]> = {};
    view.trips.filter((t) => t.status !== "cancelled").forEach((t) => (m[t.unit_id] ||= []).push(t));
    return m;
  }, [view.trips]);

  useEffect(() => {
    // прокрутить к текущему времени (или к 7:00 для других дней)
    const el = ref.current;
    if (!el) return;
    const target = nowMin > 0 && nowMin < DAY_MIN ? nowMin - 120 : 6 * 60;
    el.scrollLeft = Math.max(0, target * PX_PER_MIN);
  }, [view.date]); // eslint-disable-line react-hooks/exhaustive-deps

  const dropAt = (unitId: string, clientX: number, lane: HTMLElement) => {
    const g = grab.current;
    if (!g || !onMove) return;
    const rect = lane.getBoundingClientRect();
    const minutes = Math.round((clientX - rect.left - g.offset) / PX_PER_MIN / 5) * 5;  // шаг 5 минут
    const base = Date.parse(zonedISO(view.date, "00:00"));
    onMove(g.trip, unitId, new Date(base + minutes * 60000).toISOString());
    grab.current = null;
  };

  if (!view.units.length) return <Empty>Нет активных РБУ. Добавьте установки в справочнике «РБУ».</Empty>;
  return (
    <div className={`timeline${order && view.trips.some((t) => t.order_id === order) ? " has-order" : ""}`} ref={ref}>
      <div className="tl-grid" style={{ width: width + 150 }}>
        <div className="tl-hours" style={{ width }}>
          {Array.from({ length: 25 }, (_, h) => (
            <span key={h} style={{ left: h * 60 * PX_PER_MIN }}>{String(h).padStart(2, "0")}</span>
          ))}
        </div>
        {view.units.map((u) => (
          <div className="tl-row" key={u.id}>
            <div className="lbl" style={{ position: "sticky", left: 0, background: "var(--panel)", zIndex: 3 }}>
              <b>{u.plant_name}</b>
              <span className="muted">{u.name}</span>
            </div>
            <div className={`lane${over === u.id ? " drop" : ""}`} style={{ width, backgroundSize: `${60 * PX_PER_MIN}px 100%` }}
              onDragOver={(e) => { if (onMove) { e.preventDefault(); setOver(u.id); } }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => { e.preventDefault(); setOver(null); dropAt(u.id, e.clientX, e.currentTarget); }}>
              {u.downtimes.map((d, i) => {
                const a = Math.max(0, minutesOfDay(d.start, view.date));
                const b = Math.min(DAY_MIN, minutesOfDay(d.end, view.date));
                return <div key={i} className="downtime" title="Плановый простой" style={{ left: a * PX_PER_MIN, width: (b - a) * PX_PER_MIN }} />;
              })}
              {(byUnit[u.id] || []).map((t) => {
                const a = minutesOfDay(t.slot_start, view.date);
                const b = minutesOfDay(t.slot_end, view.date);
                const cls = ["trip", tripSwatch(t.status, t.conflict_override || view.conflicts.includes(t.id)),
                  t.timing_level === "risk" ? "risk" : "", t.timing_level === "late" ? "late" : "", selected === t.id ? "sel" : "",
                  // замечание 15.10 №5: рейсы выбранной заявки подсвечены, остальные приглушены
                  order && t.order_id === order ? "of-order" : ""].join(" ");
                const draggable = !!onMove && ["planned", "assigned", "queued"].includes(t.status);
                return (
                  <div key={t.id} className={cls} style={{ left: a * PX_PER_MIN, width: Math.max(8, (b - a) * PX_PER_MIN) }}
                    draggable={draggable}
                    onDragStart={(e) => { grab.current = { trip: t, offset: e.clientX - e.currentTarget.getBoundingClientRect().left }; e.dataTransfer.effectAllowed = "move"; }}
                    onClick={() => onSelect(t)}
                    title={`№${t.order_number} ${t.customer_name}, рейс ${t.seq}/${t.trips_total}\n${t.grade}, ${t.volume_m3} м³\nПогрузка ${fmtTime(t.slot_start)}, прибытие ${fmtTime(t.planned_arrival)}${t.vehicle_plate ? "\n" + t.vehicle_plate + (t.driver_name ? ", " + t.driver_name : "") : "\nМашина не назначена"}${t.deviation_minutes && t.deviation_minutes > 0 ? `\nОтклонение ${t.deviation_minutes} мин` : ""}`}>
                    {(b - a) * PX_PER_MIN > 22 && <>{t.seq}</>}
                  </div>
                );
              })}
              {nowMin > 0 && nowMin < DAY_MIN && (
                <div className="now-line" style={{ left: nowMin * PX_PER_MIN }} />
              )}
            </div>
          </div>
        ))}
        <div style={{ height: 24, marginLeft: 150, position: "relative" }}>
          {nowMin > 0 && nowMin < DAY_MIN && (
            <span className="small" style={{ position: "absolute", left: nowMin * PX_PER_MIN - 36, color: "var(--accent)" }}>
              сейчас {fmtTime(view.now)}
            </span>
          )}
        </div>
      </div>
      <p className="sr">{fmtDay(view.date)}</p>
    </div>
  );
}
